// src/hooks/useRoleProfiles.ts - Hooks pour la gestion des profils de rôle personnalisés

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { RoleProfile, RolePermissions, normalizeRolePermissions } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';

// ===== HOOKS POUR LES PROFILS DE RÔLE =====

/**
 * Hook pour récupérer tous les profils de rôle d'un business
 */
export const useRoleProfiles = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['roleProfiles', businessId],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      const q = query(
        collection(db, COLLECTIONS.roleProfiles),
        where('businessId', '==', businessId),
        orderBy('name')
      );
      
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useRoleProfiles - Main query');
        throw error;
      });

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          businessId: data.businessId,
          name: data.name,
          description: data.description,
          permissions: normalizeRolePermissions(data.permissions as RolePermissions),
          isActive: data.isActive ?? true,
          isDefault: data.isDefault ?? false,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          usageCount: data.usageCount ?? 0,
        } as RoleProfile;
      });
    },
    enabled: !!businessId && (permissions?.canViewRoleProfiles ?? false),
  });
};

/**
 * Hook pour récupérer tous les profils de rôle d'un business spécifique (pour Super Admin)
 */
export const useRoleProfilesByBusinessId = (businessId: string | undefined) => {
  return useQuery({
    queryKey: ['roleProfiles', businessId],
    queryFn: async () => {
      if (!businessId) {
        return [];
      }

      const q = query(
        collection(db, COLLECTIONS.roleProfiles),
        where('businessId', '==', businessId),
        orderBy('name')
      );
      
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useRoleProfilesByBusinessId - Main query');
        throw error;
      });

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          businessId: data.businessId,
          name: data.name,
          description: data.description,
          permissions: normalizeRolePermissions(data.permissions as RolePermissions),
          isActive: data.isActive ?? true,
          isDefault: data.isDefault ?? false,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          usageCount: data.usageCount ?? 0,
        } as RoleProfile;
      });
    },
    enabled: !!businessId,
  });
};

/**
 * Hook pour récupérer un profil de rôle spécifique
 */
export const useRoleProfile = (roleProfileId: string | undefined) => {
  return useQuery({
    queryKey: ['roleProfile', roleProfileId],
    queryFn: async () => {
      if (!roleProfileId) {
        throw new Error('roleProfileId est requis');
      }

      const docRef = doc(db, COLLECTIONS.roleProfiles, roleProfileId);
      const docSnap = await getDoc(docRef).catch(error => {
        handleFirestoreError(error, 'useRoleProfile - Get document');
        throw error;
      });

      if (!docSnap.exists()) {
        throw new Error('Profil de rôle introuvable');
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        businessId: data.businessId,
        name: data.name,
        description: data.description,
        permissions: normalizeRolePermissions(data.permissions as RolePermissions),
        isActive: data.isActive ?? true,
        isDefault: data.isDefault ?? false,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        usageCount: data.usageCount ?? 0,
      } as RoleProfile;
    },
    enabled: !!roleProfileId,
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
  });
};

/**
 * Hook pour créer un profil de rôle
 */
export const useAddRoleProfile = (overrideBusinessId?: string) => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = overrideBusinessId || currentBusiness?.id; // ✅ NOUVEAU : Permettre override pour Super Admin

  return useMutation({
    mutationFn: async (profile: Omit<RoleProfile, 'id' | 'businessId' | 'createdAt' | 'updatedAt' | 'createdBy' | 'usageCount'>) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      // ✅ NOUVEAU : Pour Super Admin, utiliser l'ID du Super Admin comme createdBy
      const createdBy = userProfile?.id || 'system';

      const profileData = {
        businessId,
        name: profile.name.trim(),
        description: profile.description?.trim() || null,
        permissions: profile.permissions,
        isActive: profile.isActive ?? true,
        isDefault: profile.isDefault ?? false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy,
        usageCount: 0,
      };

      // Supprimer les champs null
      Object.keys(profileData).forEach(key => {
        if (profileData[key as keyof typeof profileData] === null) {
          delete profileData[key as keyof typeof profileData];
        }
      });

      const docRef = await addDoc(collection(db, COLLECTIONS.roleProfiles), profileData);
      return { id: docRef.id, businessId };
    },
    onSuccess: (result) => {
      // Invalider les queries pour le businessId du profil créé
      queryClient.invalidateQueries({ queryKey: ['roleProfiles', result.businessId] });
      // Invalider aussi les queries générales
      queryClient.invalidateQueries({ queryKey: ['roleProfiles'] });
    },
  });
};

/**
 * Hook pour mettre à jour un profil de rôle
 */
export const useUpdateRoleProfile = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<RoleProfile> & { id: string }) => {
      // Récupérer le businessId du profil avant la mise à jour
      const docRef = doc(db, COLLECTIONS.roleProfiles, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Profil de rôle introuvable');
      }

      const existingData = docSnap.data();
      const profileBusinessId = existingData.businessId;

      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      if (data.name !== undefined) {
        updateData.name = data.name.trim();
      }
      if (data.description !== undefined) {
        updateData.description = data.description?.trim() || null;
      }
      if (data.permissions !== undefined) {
        updateData.permissions = data.permissions;
      }
      if (data.isActive !== undefined) {
        updateData.isActive = data.isActive;
      }

      // Supprimer les champs null
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null) {
          delete updateData[key];
        }
      });

      await updateDoc(docRef, updateData);
      
      // Retourner le businessId pour l'invalidation
      return profileBusinessId;
    },
    onSuccess: (profileBusinessId) => {
      // Invalider les queries pour le businessId du profil modifié
      queryClient.invalidateQueries({ queryKey: ['roleProfiles', profileBusinessId] });
      // Invalider aussi les queries générales (pour le cas où on est dans le même business)
      queryClient.invalidateQueries({ queryKey: ['roleProfiles'] });
      // Invalider la query du profil spécifique
      queryClient.invalidateQueries({ queryKey: ['roleProfile'] });
    },
  });
};

/**
 * Hook pour supprimer un profil de rôle
 */
export const useDeleteRoleProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleProfileId: string) => {
      // Vérifier que le profil n'est pas système
      const docRef = doc(db, COLLECTIONS.roleProfiles, roleProfileId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Profil de rôle introuvable');
      }

      const data = docSnap.data();
      const profileBusinessId = data.businessId;

      // Vérifier qu'aucun employé n'utilise ce profil
      if (data.usageCount > 0) {
        throw new Error(`Impossible de supprimer ce profil : ${data.usageCount} employé(s) l'utilise(nt)`);
      }

      await deleteDoc(docRef);
      return profileBusinessId;
    },
    onSuccess: (profileBusinessId) => {
      // Invalider les queries pour le businessId du profil supprimé
      queryClient.invalidateQueries({ queryKey: ['roleProfiles', profileBusinessId] });
      // Invalider aussi les queries générales
      queryClient.invalidateQueries({ queryKey: ['roleProfiles'] });
    },
  });
};

/**
 * Hook pour dupliquer un profil de rôle
 */
export const useDuplicateRoleProfile = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();

  return useMutation({
    mutationFn: async (roleProfileId: string) => {
      // Récupérer le profil à dupliquer
      const docRef = doc(db, COLLECTIONS.roleProfiles, roleProfileId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Profil de rôle introuvable');
      }

      const data = docSnap.data();
      const businessId = data.businessId;
      const createdBy = userProfile?.id || 'system';
      
      const originalProfile = {
        id: docSnap.id,
        ...data,
      } as RoleProfile;

      // Créer un nouveau profil avec le même contenu mais un nouveau nom
      const profileData = {
        businessId,
        name: `${originalProfile.name} (Copie)`,
        description: originalProfile.description?.trim() || null,
        permissions: originalProfile.permissions,
        isActive: originalProfile.isActive ?? true,
        isDefault: false, // Les copies ne sont jamais par défaut
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy,
        usageCount: 0,
      };

      // Supprimer les champs null
      Object.keys(profileData).forEach(key => {
        if (profileData[key as keyof typeof profileData] === null) {
          delete profileData[key as keyof typeof profileData];
        }
      });

      const newDocRef = await addDoc(collection(db, COLLECTIONS.roleProfiles), profileData);
      return { id: newDocRef.id, businessId };
    },
    onSuccess: (result) => {
      // Invalider les queries pour le businessId du profil dupliqué
      queryClient.invalidateQueries({ queryKey: ['roleProfiles', result.businessId] });
      // Invalider aussi les queries générales
      queryClient.invalidateQueries({ queryKey: ['roleProfiles'] });
    },
  });
};

